import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  Chip,
  Container,
  Grid,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  CircularProgress,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Page from 'src/components/Page';
import { payrollImportService, formatMoney } from 'src/services/payroll';
import type { PayrollImportStatus } from 'src/types/payroll.types';

const STATUS_COLOR: Record<PayrollImportStatus, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  preview: 'info',
  approved: 'success',
  partial: 'warning',
  cancelled: 'default',
};

function ActionCard({
  title,
  description,
  icon,
  color,
  onClick,
  cta,
}: {
  title: string;
  description: string;
  icon: string;
  color: string;
  onClick: () => void;
  cta: string;
}) {
  return (
    <Card
      elevation={0}
      onClick={onClick}
      sx={{
        p: 2,
        height: '100%',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: alpha(color, 0.2),
        background: `linear-gradient(160deg, ${alpha(color, 0.06)} 0%, #fff 60%)`,
        cursor: 'pointer',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 8px 20px ${alpha(color, 0.12)}`,
        },
      }}
    >
      <Stack spacing={1} height="100%">
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 1.5,
              bgcolor: alpha(color, 0.12),
              color,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            <Icon icon={icon} width={22} />
          </Box>
          <Typography variant="subtitle1" fontWeight={800} color="#1A202C" lineHeight={1.2}>
            {title}
          </Typography>
        </Stack>

        <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, display: 'block', lineHeight: 1.3 }}>
          {description}
        </Typography>

        <Button
          endIcon={<Icon icon="mdi:arrow-right" />}
          sx={{
            alignSelf: 'flex-start',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: '0.75rem',
            color,
            p: 0,
            minWidth: 0,
          }}
        >
          {cta}
        </Button>
      </Stack>
    </Card>
  );
}

export default function PayrollHub() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['payroll-imports', page],
    queryFn: () => payrollImportService.listImports(page, 10),
  });

  const items = data?.items || data?.data || [];
  const totalPages = data?.totalPages || Math.ceil((data?.total || 0) / 10) || 1;

  return (
    <Page title="Scholar Dollar Pay">
      <Container
        maxWidth={false}
        sx={{ width: '100%', px: 1, mx: 0 }}
      >
        <Stack spacing={0.25} sx={{ mb: 1.5 }}>
          <Typography variant="h5" fontWeight={800} color="#1A202C">
            Scholar Dollar Pay
          </Typography>
          <Typography variant="caption" color="text.secondary" maxWidth={640}>
            Import biweekly grades, withhold tax for Tax Day, and deposit net pay — same math as Google Sheets, zero busywork.
          </Typography>
        </Stack>

        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          <Grid item xs={12} md={4}>
            <ActionCard
              title="Import Scholar Dollar Pay"
              description="Upload CSV of Period 1–8 grades, preview gross / tax / net, then deposit net Scholar Dollars."
              icon="mdi:file-upload-outline"
              color="#297041"
              cta="Start pay import"
              onClick={() => navigate('/admin/payroll/import')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ActionCard
              title="Student Account Setup"
              description="Create student accounts from a Student ID CSV so payroll matching works seamlessly."
              icon="mdi:account-multiple-plus-outline"
              color="#2B6CB0"
              cta="Import students"
              onClick={() => navigate('/admin/payroll/roster')}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <ActionCard
              title="Tax Day Export"
              description="Download YTD gross, tax withheld, and net per student from stored paycheck snapshots."
              icon="mdi:file-chart-outline"
              color="#805AD5"
              cta="Export YTD"
              onClick={() => navigate('/admin/payroll/tax-day')}
            />
          </Grid>
        </Grid>

        <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
          <Box
            sx={{
              px: 2,
              py: 1,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #E2E8F0',
              bgcolor: alpha('#297041', 0.03),
            }}
          >
            <Typography variant="subtitle1" fontWeight={800} color="#1A202C">
              Import History
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<Icon icon="mdi:plus" />}
              onClick={() => navigate('/admin/payroll/import')}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.75rem',
                borderRadius: 1.5,
                bgcolor: '#EDB42E',
                color: '#1A1A1A',
                px: 1.5,
                py: 0.5,
                '&:hover': { bgcolor: '#d9a429' },
              }}
            >
              New Import
            </Button>
          </Box>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#F7FAFC' }}>
                  <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>Import ID / Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>Date</TableCell>
                  <TableCell sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>
                    Gross Pay
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>
                    Tax Withheld
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>
                    Net Deposited
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, py: 1, fontSize: '0.75rem' }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} sx={{ color: '#297041' }} />
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary', fontSize: '0.8125rem' }}>
                      No payroll imports found. Click "New Import" above to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((row) => {
                    const gross = row.totals?.total_gross ?? row.totalgrosspay ?? 0;
                    const tax = row.totals?.total_tax ?? row.totaltaxwithheld ?? 0;
                    const net = row.totals?.total_net ?? row.totalnetpay ?? 0;
                    const displayName =
                      row.name || row.file_name || row.pay_period_key || `Import #${row.id.substring(0, 8)}`;

                    return (
                      <TableRow key={row.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell sx={{ py: 1, fontWeight: 700, fontSize: '0.8125rem' }}>
                          {displayName}
                        </TableCell>
                        <TableCell sx={{ py: 1, fontSize: '0.8125rem', color: 'text.secondary' }}>
                          {new Date(row.createdat).toLocaleDateString()}
                        </TableCell>
                        <TableCell sx={{ py: 1 }}>
                          <Chip
                            label={row.status}
                            size="small"
                            color={STATUS_COLOR[row.status] || 'default'}
                            sx={{ textTransform: 'capitalize', height: 22, fontSize: '0.6875rem', fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1, fontSize: '0.8125rem' }}>
                          {formatMoney(gross)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1, fontSize: '0.8125rem', color: '#D69E2E' }}>
                          {formatMoney(tax)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1, fontWeight: 700, color: '#2F855A', fontSize: '0.8125rem' }}>
                          {formatMoney(net)}
                        </TableCell>
                        <TableCell align="right" sx={{ py: 1 }}>
                          <Button
                            size="small"
                            onClick={() => navigate(`/admin/payroll/imports/${row.id}`)}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.2 }}
                          >
                            View Details
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #E2E8F0' }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button
                  size="small"
                  disabled={page === 1}
                  onClick={() => setPage((p) => p - 1)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Previous
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Page {page} of {totalPages}
                </Typography>
                <Button
                  size="small"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                >
                  Next
                </Button>
              </Stack>
            </Box>
          )}
        </Card>
      </Container>
    </Page>
  );
}
